import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Space,
  App,
  Spin,
  Typography,
  Card,
  Avatar,
  Tag,
  Descriptions,
  Timeline,
  Collapse,
  Empty,
  Steps,
} from 'antd';
import {
  ArrowLeftOutlined,
  UserOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ApiOutlined,
  BellOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import {
  TradevestOnboardingLog,
  TradevestOnboardingEvent,
  TradevestRetryStep,
  UserDetailResponse,
} from '@mygate/common';
import { usersService } from '@/services/users';
import { formatDateTime } from '@/utils/date';

const { Text } = Typography;

const RETRY_STEP_COLORS: Record<TradevestRetryStep['status'], string> = {
  success: 'green',
  error: 'red',
  info: 'blue',
  skipped: 'default',
};

/** MANUAL_RETRY events carry the steps the retry executed — including what Tradevest answered for
 *  each product, which is the whole story when an activation is late (MYG-677). Older events only
 *  have stepsCount, so the metadata is narrowed rather than trusted. */
function readRetrySteps(metadata: Record<string, unknown> | undefined): TradevestRetryStep[] {
  const steps = metadata?.steps;
  if (!Array.isArray(steps)) return [];
  return steps.filter(
    (step): step is TradevestRetryStep =>
      !!step &&
      typeof step === 'object' &&
      typeof (step as TradevestRetryStep).label === 'string' &&
      (step as TradevestRetryStep).status in RETRY_STEP_COLORS,
  );
}

function getEventIcon(type: string) {
  if (type.includes('ERROR')) return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
  if (type.includes('WEBHOOK')) return <BellOutlined style={{ color: '#722ed1' }} />;
  if (type.includes('API')) return <ApiOutlined style={{ color: '#1890ff' }} />;
  if (type.includes('COMPLETED') || type.includes('CREATED'))
    return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
  return <InfoCircleOutlined style={{ color: '#1890ff' }} />;
}

function getEventColor(type: string): string {
  if (type.includes('ERROR')) return 'red';
  if (type.includes('WEBHOOK')) return 'purple';
  if (type.includes('API_REQUEST')) return 'blue';
  if (type.includes('API_RESPONSE')) return 'cyan';
  return 'green';
}

function getStatusColor(log: TradevestOnboardingLog): string {
  if (log.isCompleted) return 'green';
  if (log.isFailed) return 'red';
  return 'blue';
}

function getStatusIcon(log: TradevestOnboardingLog): React.ReactNode {
  if (log.isCompleted) return <CheckCircleOutlined />;
  if (log.isFailed) return <CloseCircleOutlined />;
  return <ExclamationCircleOutlined />;
}

/** Status descriptions for admin reference — explains what each step does and what triggers the next */
const STATUS_FLOW_STEPS: { status: string; title: string; description: string; trigger: string }[] = [
  {
    status: 'not_started',
    title: 'Not Started',
    description: 'Initial state. User has not begun Tradevest onboarding.',
    trigger: 'User taps "Open Trading Account" in the mobile app',
  },
  {
    status: 'kyc_required',
    title: 'KYC Required',
    description: 'User must complete KYC (PostIdent) before Tradevest onboarding can proceed.',
    trigger: 'User completes PostIdent KYC verification',
  },
  {
    status: 'natural_person_created',
    title: 'Natural Person Created',
    description:
      "A Natural Person entity was created on Tradevest with the user's identity data from PostIdent. " +
      'NP status goes through RECEIVED → (REVIEW) → CREATED; a compliance REVIEW can take minutes or hours. ' +
      'Bank details submitted meanwhile are kept ("customer_creation_deferred" in the timeline) and the ' +
      'customer role is created automatically once Tradevest releases the NP.',
    trigger: 'User submits bank details; the customer role follows as soon as the NP is CREATED or ACTIVE',
  },
  {
    status: 'customer_created',
    title: 'Customer Created',
    description:
      'A Customer role and Identity Verification were created on Tradevest. ' +
      'User can now accept terms and sign documents in the mobile app.',
    trigger: 'User taps "Accept Terms" → signs TERMS_AND_CONDITIONS + DATA_PRIVACY_POLICY',
  },
  {
    status: 'documents_signing_pending',
    title: 'Documents Signing Pending',
    description:
      "The customer's consent is recorded and the signatures were submitted (HTTP 202 Accepted) once the " +
      'customer role existed at Tradevest. A signature Tradevest drops (VALIDATION_ERROR webhook) is ' +
      're-submitted automatically — shortly after the verdict, then at a doubling interval by the 5-minute poll, or at once by Retry — up to 12 attempts.',
    trigger: 'Tradevest shows both documents as signed (webhook or poll)',
  },
  {
    status: 'documents_signed',
    title: 'Documents Signed',
    description: 'Both Terms & Conditions and Privacy Policy are confirmed signed by Tradevest.',
    trigger: 'System automatically submits onboarding request to Tradevest',
  },
  {
    status: 'onboarding_initiated',
    title: 'Onboarding Initiated',
    description: 'An onboarding request was submitted to Tradevest for approval.',
    trigger: 'Scheduler job (POLL) detects onboarding status is APPROVED',
  },
  {
    status: 'onboarding_approved',
    title: 'Onboarding Approved',
    description: 'Tradevest approved the onboarding. System creates Balance Account and Crypto Wallet products.',
    trigger: 'Both products successfully created on Tradevest',
  },
  {
    status: 'products_created',
    title: 'Products Created',
    description: 'Balance Account and Crypto Wallet product requests submitted to Tradevest.',
    trigger: 'CUSTOMER_PRODUCT_NOTIFICATION webhooks received with ACTIVE status for both products',
  },
  {
    status: 'awaiting_products_activation',
    title: 'Awaiting Products Activation',
    description: 'Waiting for Tradevest to activate both products (Balance Account + Crypto Wallet).',
    trigger: 'Both products report ACTIVE via webhook → IBAN and BIC are extracted',
  },
  {
    status: 'completed',
    title: 'Completed',
    description: 'Onboarding fully completed. User has an active trading account with IBAN and crypto wallet.',
    trigger: '—',
  },
];

/** Map of failed statuses to human-readable descriptions */
const FAILED_STATUS_DESCRIPTIONS: Record<string, string> = {
  tax_id_required:
    'Tradevest requires a tax ID for this tax residency and none is on file — enter it under Edit User Details; onboarding resumes by itself. Do not Reset.',
  natural_person_creation_failed: 'Failed to create Natural Person on Tradevest API',
  identity_verification_failed: 'Identity verification rejected or PostIdent PDF missing',
  bank_account_validation_error:
    'Bank details missing, or Tradevest refused the customer role they were sent with (see the CUSTOMER_REJECTED_BY_TRADEVEST event for its reason) — the app asks the user for them again',
  natural_person_not_found_for_customer: 'Natural Person ID not set when trying to create Customer',
  customer_id_extraction_failed: 'Customer exists (409 conflict) but ID could not be extracted',
  customer_creation_failed:
    'Customer role could not be created: the natural person was refused, Tradevest kept failing for 12 attempts, or a role it had already refused could not be corrected (CUSTOMER_ROLE_UNRECOVERABLE — Tradevest has to reset it). Retry asks the customer for bank details again — no Reset needed',
  terms_signing_failed: 'Failed to sign Terms and Conditions',
  privacy_signing_failed: 'Failed to sign Privacy Policy',
  documents_signing_failed:
    'Tradevest rejected a document, or did not record the signature after 12 attempts. Retry re-submits with a fresh count',
  onboarding_creation_failed: 'Failed to create onboarding request on Tradevest',
  onboarding_rejected: 'Tradevest rejected the onboarding request',
  onboarding_invalid: 'Onboarding data validation failed on Tradevest side',
  onboarding_timeout: 'Onboarding approval not received within 1 hour timeout',
  product_creation_failed: 'Failed to create Balance Account or Crypto Wallet products',
  products_activation_timeout: 'Product ACTIVE webhooks not received within 30 minute timeout',
  unknown_error: 'An unexpected error occurred during onboarding',
};

function getCurrentStepIndex(status: string | undefined): number {
  if (!status) return -1;
  return STATUS_FLOW_STEPS.findIndex(s => s.status === status);
}

export function TradevestOnboardingLogPage() {
  const { message } = App.useApp();
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [userDetail, setUserDetail] = useState<UserDetailResponse | null>(null);
  const [logData, setLogData] = useState<TradevestOnboardingLog | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  useEffect(() => {
    if (userId) {
      fetchUserDetails();
      fetchTradevestLog();
    }
  }, [userId]);

  const fetchUserDetails = async () => {
    if (!userId) return;

    try {
      const response = await usersService.getUserDetails(userId);
      if (response.data) {
        setUserDetail(response.data);
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  const fetchTradevestLog = async () => {
    if (!userId) return;

    setLoading(true);
    try {
      const response = await usersService.getTradevestOnboardingLog(userId);
      if (response.success) {
        setLogData(response.data ?? null);
        setLastUpdated(new Date());
      }
    } catch (error) {
      message.error('Failed to fetch Tradevest onboarding log');
      console.error('Error fetching Tradevest log:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderEventDetails = (event: TradevestOnboardingEvent) => {
    const items = [];

    if (event.httpRequest) {
      items.push(
        <Descriptions.Item key="request" label="HTTP Request" span="filled">
          <Text code>
            {event.httpRequest.method} {event.httpRequest.url}
          </Text>
          {event.httpRequest.body ? (
            <Collapse
              size="small"
              style={{ marginTop: 8 }}
              items={[
                {
                  key: 'body',
                  label: 'Request Body',
                  children: (
                    <pre style={{ fontSize: 11, margin: 0, maxHeight: 200, overflow: 'auto' }}>
                      {JSON.stringify(event.httpRequest.body, null, 2)}
                    </pre>
                  ),
                },
              ]}
            />
          ) : null}
        </Descriptions.Item>,
      );
    }

    if (event.httpResponse) {
      items.push(
        <Descriptions.Item key="response" label="HTTP Response" span="filled">
          <Space>
            <Tag color={event.httpResponse.statusCode >= 400 ? 'red' : 'green'}>{event.httpResponse.statusCode}</Tag>
            <Text type="secondary">{event.httpResponse.durationMs}ms</Text>
          </Space>
          {event.httpResponse.body ? (
            <Collapse
              size="small"
              style={{ marginTop: 8 }}
              items={[
                {
                  key: 'body',
                  label: 'Response Body',
                  children: (
                    <pre style={{ fontSize: 11, margin: 0, maxHeight: 200, overflow: 'auto' }}>
                      {JSON.stringify(event.httpResponse.body, null, 2)}
                    </pre>
                  ),
                },
              ]}
            />
          ) : null}
        </Descriptions.Item>,
      );
    }

    if (event.webhook) {
      items.push(
        <Descriptions.Item key="webhook" label="Webhook" span="filled">
          <Tag color="purple">{event.webhook.eventType}</Tag>
          {event.webhook.payload ? (
            <Collapse
              size="small"
              style={{ marginTop: 8 }}
              items={[
                {
                  key: 'payload',
                  label: 'Webhook Payload',
                  children: (
                    <pre style={{ fontSize: 11, margin: 0, maxHeight: 200, overflow: 'auto' }}>
                      {JSON.stringify(event.webhook.payload, null, 2)}
                    </pre>
                  ),
                },
              ]}
            />
          ) : null}
        </Descriptions.Item>,
      );
    }

    if (event.statusChange) {
      items.push(
        <Descriptions.Item key="status" label="Status Change" span="filled">
          <Space>
            <Tag>{event.statusChange.previousStatus || 'none'}</Tag>
            <Text type="secondary">→</Text>
            <Tag color="blue">{event.statusChange.newStatus}</Tag>
          </Space>
        </Descriptions.Item>,
      );
    }

    if (event.error) {
      items.push(
        <Descriptions.Item key="error" label="Error" span="filled">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text type="danger">
              <strong>{event.error.code}</strong>: {event.error.message}
            </Text>
            {event.error.stack && (
              <Collapse
                size="small"
                items={[
                  {
                    key: 'stack',
                    label: 'Stack Trace',
                    children: (
                      <pre style={{ fontSize: 11, margin: 0, maxHeight: 150, overflow: 'auto' }}>
                        {event.error.stack}
                      </pre>
                    ),
                  },
                ]}
              />
            )}
          </Space>
        </Descriptions.Item>,
      );
    }

    if (event.entityIds && Object.keys(event.entityIds).length > 0) {
      const entityEntries = Object.entries(event.entityIds).filter(([, v]) => v);
      if (entityEntries.length > 0) {
        items.push(
          <Descriptions.Item key="entities" label="Entity IDs" span="filled">
            <Space wrap>
              {entityEntries.map(([key, value]) => (
                <Tag key={key}>
                  {key}: <Text copyable={{ text: value }}>{value}</Text>
                </Tag>
              ))}
            </Space>
          </Descriptions.Item>,
        );
      }
    }

    const retrySteps = readRetrySteps(event.metadata);
    if (retrySteps.length > 0) {
      items.push(
        <Descriptions.Item key="retrySteps" label="Retry Steps" span="filled">
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            {retrySteps.map((step, index) => (
              <Space key={`${step.label}-${index}`} size={6}>
                <Tag color={RETRY_STEP_COLORS[step.status]}>{step.status}</Tag>
                <Text>{step.label}</Text>
                {step.detail && <Text type="secondary">{step.detail}</Text>}
              </Space>
            ))}
          </Space>
        </Descriptions.Item>,
      );
    }

    if (event.metadata && Object.keys(event.metadata).length > 0) {
      items.push(
        <Descriptions.Item key="metadata" label="Metadata" span="filled">
          <Collapse
            size="small"
            items={[
              {
                key: 'metadata',
                label: 'View Metadata',
                children: (
                  <pre style={{ fontSize: 11, margin: 0, maxHeight: 150, overflow: 'auto' }}>
                    {JSON.stringify(event.metadata, null, 2)}
                  </pre>
                ),
              },
            ]}
          />
        </Descriptions.Item>,
      );
    }

    return items.length > 0 ? (
      <Descriptions size="small" column={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 3, xxl: 3 }} style={{ marginTop: 8 }}>
        {items}
      </Descriptions>
    ) : null;
  };

  return (
    <>
      <div className="page-toolbar">
        <Space>
          <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate('/users')}>
            Kunden
          </Button>
          <Button type="text" onClick={() => window.close()}>
            Fenster schließen
          </Button>
        </Space>

        {userDetail && (
          <Space align="center">
            <Avatar size="large" icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
            <div style={{ lineHeight: 1.5 }}>
              <Text strong style={{ fontSize: '14px' }}>
                {userDetail.user.firstName} {userDetail.user.lastName}
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {userDetail.user.phoneNumber} | {userDetail.user.email || 'No email'}
              </Text>
            </div>
          </Space>
        )}
      </div>

      <>
        <div
          style={{
            marginBottom: 16,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Space>
            <Button type="primary" icon={<ReloadOutlined />} onClick={fetchTradevestLog} loading={loading}>
              Refresh
            </Button>
            {lastUpdated && <Text type="secondary">Last updated: {formatDateTime(lastUpdated.toISOString())}</Text>}
          </Space>
          {logData && (
            <Tag
              color={getStatusColor(logData)}
              style={{ fontSize: '14px', padding: '4px 12px' }}
              icon={getStatusIcon(logData)}
            >
              {logData.currentStatus || 'not_started'}
            </Tag>
          )}
        </div>

        {loading && !logData ? (
          <Card>
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">Fetching Tradevest onboarding log...</Text>
              </div>
            </div>
          </Card>
        ) : !logData ? (
          <Card>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Space direction="vertical">
                  <Text type="secondary">No Tradevest onboarding log found for this user</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    The user may not have started Tradevest onboarding yet
                  </Text>
                </Space>
              }
            />
          </Card>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {/* Summary Card */}
            <Card title="Summary">
              <Descriptions bordered column={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 3, xxl: 3 }} size="small">
                <Descriptions.Item label="User ID">{logData.userId}</Descriptions.Item>
                <Descriptions.Item label="Current Status">
                  <Tag color={getStatusColor(logData)}>{logData.currentStatus || 'not_started'}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Total Events">{logData.summary?.totalEvents || 0}</Descriptions.Item>
                <Descriptions.Item label="API Calls">{logData.summary?.apiCallCount || 0}</Descriptions.Item>
                <Descriptions.Item label="Webhooks">{logData.summary?.webhookCount || 0}</Descriptions.Item>
                <Descriptions.Item label="Errors">{logData.summary?.errorCount || 0}</Descriptions.Item>
                {logData.entityIds?.naturalPersonId && (
                  <Descriptions.Item label="Natural Person ID">
                    <Text copyable>{logData.entityIds.naturalPersonId}</Text>
                  </Descriptions.Item>
                )}
                {logData.entityIds?.customerId && (
                  <Descriptions.Item label="Customer ID">
                    <Text copyable>{logData.entityIds.customerId}</Text>
                  </Descriptions.Item>
                )}
                {logData.entityIds?.onboardingId && (
                  <Descriptions.Item label="Onboarding ID">
                    <Text copyable>{logData.entityIds.onboardingId}</Text>
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Created At">{formatDateTime(logData.createdAt)}</Descriptions.Item>
                <Descriptions.Item label="Updated At">{formatDateTime(logData.updatedAt)}</Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Onboarding Flow Reference */}
            <Collapse
              items={[
                {
                  key: 'flow',
                  label: (
                    <Space>
                      <QuestionCircleOutlined />
                      <Text strong>Onboarding Flow Reference</Text>
                      <Text type="secondary">— click to see what each step does</Text>
                    </Space>
                  ),
                  children: (
                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                      <div>
                        <Text strong style={{ fontSize: 13 }}>
                          Happy Path (progress states):
                        </Text>
                        <Steps
                          direction="vertical"
                          size="small"
                          current={getCurrentStepIndex(logData.currentStatus)}
                          status={logData.isFailed ? 'error' : logData.isCompleted ? 'finish' : 'process'}
                          style={{ marginTop: 12 }}
                          items={STATUS_FLOW_STEPS.map(step => ({
                            title: (
                              <Space>
                                <Text strong>{step.title}</Text>
                                <Tag style={{ fontSize: 11 }}>{step.status}</Tag>
                              </Space>
                            ),
                            description: (
                              <div style={{ maxWidth: 600 }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  {step.description}
                                </Text>
                                {step.trigger !== '—' && (
                                  <div style={{ marginTop: 4 }}>
                                    <Text style={{ fontSize: 12 }}>
                                      <Text strong style={{ fontSize: 12 }}>
                                        Next trigger:{' '}
                                      </Text>
                                      {step.trigger}
                                    </Text>
                                  </div>
                                )}
                              </div>
                            ),
                          }))}
                        />
                      </div>
                      <div>
                        <Text strong style={{ fontSize: 13 }}>
                          Failed States:
                        </Text>
                        <Descriptions
                          bordered
                          size="small"
                          column={1}
                          style={{ marginTop: 8 }}
                          items={Object.entries(FAILED_STATUS_DESCRIPTIONS).map(([status, desc]) => ({
                            key: status,
                            label: <Tag color={status === logData.currentStatus ? 'red' : 'default'}>{status}</Tag>,
                            children: <Text style={{ fontSize: 12 }}>{desc}</Text>,
                          }))}
                        />
                      </div>
                    </Space>
                  ),
                },
              ]}
            />

            {/* User Snapshot Card */}
            {logData.userSnapshot && (
              <Card title="User Model Snapshot" size="small">
                <Descriptions bordered column={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 3, xxl: 3 }} size="small">
                  <Descriptions.Item label="Natural Person ID">
                    {logData.userSnapshot.naturalPersonId ? (
                      <Text copyable>{logData.userSnapshot.naturalPersonId}</Text>
                    ) : (
                      <Text type="secondary">-</Text>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Customer ID">
                    {logData.userSnapshot.customerId ? (
                      <Text copyable>{logData.userSnapshot.customerId}</Text>
                    ) : (
                      <Text type="secondary">-</Text>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Onboarding ID">
                    {logData.userSnapshot.onboardingId ? (
                      <Text copyable>{logData.userSnapshot.onboardingId}</Text>
                    ) : (
                      <Text type="secondary">-</Text>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Balance Account ID">
                    {logData.userSnapshot.balanceAccountId ? (
                      <Text copyable>{logData.userSnapshot.balanceAccountId}</Text>
                    ) : (
                      <Text type="secondary">-</Text>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Crypto Wallet ID">
                    {logData.userSnapshot.cryptoWalletId ? (
                      <Text copyable>{logData.userSnapshot.cryptoWalletId}</Text>
                    ) : (
                      <Text type="secondary">-</Text>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Job ID">
                    {logData.userSnapshot.jobId ? (
                      <Text copyable>{logData.userSnapshot.jobId}</Text>
                    ) : (
                      <Text type="secondary">-</Text>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="IBAN">
                    {logData.userSnapshot.iban ? (
                      <Text copyable>{logData.userSnapshot.iban}</Text>
                    ) : (
                      <Text type="secondary">-</Text>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="BIC">
                    {logData.userSnapshot.bic || <Text type="secondary">-</Text>}
                  </Descriptions.Item>
                  <Descriptions.Item label="Provisioning Status">
                    <Tag
                      color={
                        logData.userSnapshot.provisioningStatus === 'completed'
                          ? 'green'
                          : logData.userSnapshot.provisioningStatus === 'failed'
                            ? 'red'
                            : 'blue'
                      }
                    >
                      {logData.userSnapshot.provisioningStatus || 'not_started'}
                    </Tag>
                  </Descriptions.Item>
                  {logData.userSnapshot.provisioningError && (
                    <Descriptions.Item label="Provisioning Error" span="filled">
                      <Text type="danger">{logData.userSnapshot.provisioningError}</Text>
                    </Descriptions.Item>
                  )}
                  {logData.userSnapshot.provisioningStartedAt && (
                    <Descriptions.Item label="Started At">
                      {formatDateTime(logData.userSnapshot.provisioningStartedAt)}
                    </Descriptions.Item>
                  )}
                  {logData.userSnapshot.provisioningCompletedAt && (
                    <Descriptions.Item label="Completed At">
                      {formatDateTime(logData.userSnapshot.provisioningCompletedAt)}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Snapshot Captured">
                    {formatDateTime(logData.userSnapshot.capturedAt)}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}

            {/* Events Timeline */}
            <Card title={`Events (${logData.events?.length || 0})`}>
              {logData.events && logData.events.length > 0 ? (
                <Timeline
                  mode="left"
                  items={logData.events.map((event, index) => ({
                    key: event.eventId || index,
                    dot: getEventIcon(event.type),
                    color: getEventColor(event.type),
                    label: (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {formatDateTime(event.timestamp)}
                      </Text>
                    ),
                    children: (
                      <Card
                        size="small"
                        style={{
                          backgroundColor: event.type.includes('ERROR') ? '#fff2f0' : '#fafafa',
                          borderColor: event.type.includes('ERROR') ? '#ffccc7' : '#d9d9d9',
                        }}
                      >
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Space>
                            <Tag color={getEventColor(event.type)}>{event.type}</Tag>
                            {event.severity && event.severity !== 'INFO' && (
                              <Tag color={event.severity === 'ERROR' ? 'red' : 'orange'}>{event.severity}</Tag>
                            )}
                          </Space>
                          {event.description && <Text>{event.description}</Text>}
                          {renderEventDetails(event)}
                        </Space>
                      </Card>
                    ),
                  }))}
                />
              ) : (
                <Empty description="No events recorded" />
              )}
            </Card>

            {/* Raw JSON Card */}
            <Card title="Raw JSON">
              <Collapse
                items={[
                  {
                    key: 'raw',
                    label: 'View Raw Log Data',
                    children: (
                      <pre
                        style={{
                          backgroundColor: '#f5f5f5',
                          padding: 12,
                          borderRadius: 4,
                          fontSize: 11,
                          maxHeight: 500,
                          overflow: 'auto',
                        }}
                      >
                        {JSON.stringify(logData, null, 2)}
                      </pre>
                    ),
                  },
                ]}
              />
            </Card>
          </Space>
        )}
      </>
    </>
  );
}
